import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  Card,
  Avatar,
  InputAdornment,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { Search, FileDownload, Email, Phone } from '@mui/icons-material';
import axios, { AxiosResponse } from 'axios';
import { newServer } from 'constants/constants';
import { useLogin } from '../Contexts/AuthProvider/AuthProvider';

interface WaitlistEntry {
  id: number;
  first_name: string;
  email: string;
  phone: string;
  created_at: string;
}

interface WaitlistResponse {
  data: WaitlistEntry[];
  pagination: {
    total: number;
    currentPage: number;
    totalPages: number;
    limit: number;
  };
}

interface AxiosResponseExcel extends AxiosResponse {
  data: ArrayBuffer;
}

export default function WaitlistTable() {
  const { user } = useLogin();
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get<WaitlistResponse>(`${newServer}/waitlist?page=${page + 1}&limit=${rowsPerPage}&search=${search}`);
      setEntries(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${newServer}/waitlist/export/excel`, {
        responseType: 'arraybuffer',
        headers: {
          Authorization: `Bearer ${user?.token}`,
        }
      }) as AxiosResponseExcel;

      if (!response.data) {
        throw new Error('No data received');
      }

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `waitlist_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

    } catch (error) {
      console.error('Error downloading Excel:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, search]);

  return (
    <Box sx={{ 
      bgcolor: '#f8fafc',
      p: { xs: 2, sm: 3, md: 3 },
    }}>
      {/* Actions Bar */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <TextField
          placeholder="Search by name, email or phone..."
          variant="outlined"
          size="small"
          fullWidth={isSmall}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#64748b' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: { xs: 1, sm: 'none' },
            minWidth: { xs: '100%', sm: 280 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: '#cbd5e1' },
              '&.Mui-focused fieldset': { borderColor: '#E91E8C' },
            },
          }}
        />
        
        <Button 
          variant="contained" 
          onClick={handleExportExcel}
          startIcon={<FileDownload />}
          size="small"
          sx={{
            bgcolor: '#E91E8C',
            '&:hover': { bgcolor: '#c41874' },
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          Export Excel
        </Button>
      </Stack>

      {/* Table/Cards */}
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '40vh',
          gap: 2,
        }}>
          <CircularProgress sx={{ color: '#E91E8C' }} size={48} />
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Loading waitlist...
          </Typography>
        </Box>
      ) : isMobile ? (
        <Stack spacing={2}>
          {entries.map((entry) => (
            <Card 
              key={entry.id} 
              sx={{ 
                p: 2.5,
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                transition: 'box-shadow 0.2s',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Avatar 
                  sx={{ 
                    bgcolor: '#E91E8C',
                    width: 48,
                    height: 48,
                    fontSize: 18,
                    fontWeight: 600,
                  }}
                >
                  {entry.first_name?.[0]?.toUpperCase() || '?'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}
                  >
                    {entry.first_name}
                  </Typography>
                  
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Email sx={{ fontSize: 16, color: '#64748b' }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#64748b',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entry.email}
                      </Typography>
                    </Stack>
                    
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Phone sx={{ fontSize: 16, color: '#64748b' }} />
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {entry.phone || 'N/A'}
                      </Typography>
                    </Stack>
                  </Stack>
                  
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#94a3b8', 
                      mt: 1.5, 
                      display: 'block',
                    }}
                  >
                    {new Date(entry.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          ))}
        </Stack>
      ) : (
        <Card sx={{ 
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow 
                    key={entry.id} 
                    hover
                    sx={{
                      '&:hover': { bgcolor: '#fafafa' },
                    }}
                  >
                    <TableCell sx={{ color: '#64748b' }}>
                      #{entry.id}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar 
                          sx={{ 
                            bgcolor: '#E91E8C',
                            width: 36,
                            height: 36,
                            fontSize: 14,
                          }}
                        >
                          {entry.first_name?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                          {entry.first_name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: '#64748b' }}>{entry.email}</TableCell>
                    <TableCell sx={{ color: '#64748b' }}>{entry.phone || 'N/A'}</TableCell>
                    <TableCell sx={{ color: '#64748b' }}>
                      {new Date(entry.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

      {/* Pagination */}
      {!loading && (
        <Card sx={{ 
          mt: 2, 
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ 
            color: '#475569',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
          }}
          labelRowsPerPage={isSmall ? 'Rows:' : 'Rows per page:'}
        />
        </Card>
      )}
    </Box>
  );
}
